import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Bookmark, BookmarkPlus, Dumbbell, Loader2, Plus, Search, X } from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';

const SEARCH_LIMIT = 25;
const EXERCISE_FIELDS = 'id, name, difficulty, primary_region, video_thumbnail_url';

const formatLabel = (value) => (value ? String(value).replace(/_/g, ' ') : null);

const ExerciseThumb = ({ exercise }) => (
  exercise?.video_thumbnail_url ? (
    <img
      src={exercise.video_thumbnail_url}
      alt=""
      className="h-10 w-10 rounded object-cover flex-shrink-0 bg-muted"
      loading="lazy"
    />
  ) : (
    <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
      <Dumbbell className="h-4 w-4 text-muted-foreground" />
    </div>
  )
);

const ExerciseMeta = ({ exercise, children }) => {
  const parts = [formatLabel(exercise?.difficulty), formatLabel(exercise?.primary_region)].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-muted-foreground capitalize">
      {parts.map((p) => <span key={p}>{p}</span>)}
      {children}
    </div>
  );
};

const BookmarkedExercisesCard = ({ userId }) => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const fetchBookmarks = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_exercise_bookmarks')
        .select(`id, exercise_id, created_at, exercises(${EXERCISE_FIELDS})`)
        .eq('user_id', userId)
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setBookmarks(data || []);
    } catch (err) {
      console.error('Error fetching exercise bookmarks:', err);
      toast.error('Could not load bookmarked exercises', { description: err.message });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  useEffect(() => {
    if (!dialogOpen) return;
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const [byName, byAlias] = await Promise.all([
          supabase
            .from('exercises')
            .select(EXERCISE_FIELDS)
            .eq('is_active', true)
            .ilike('name', `%${term}%`)
            .order('name')
            .limit(SEARCH_LIMIT),
          supabase
            .from('exercises')
            .select(EXERCISE_FIELDS)
            .eq('is_active', true)
            .contains('aliases', [term])
            .limit(SEARCH_LIMIT),
        ]);
        if (byName.error) throw byName.error;
        if (cancelled) return;
        const merged = new Map();
        for (const ex of [...(byName.data || []), ...(byAlias.data || [])]) {
          if (!merged.has(ex.id)) merged.set(ex.id, ex);
        }
        setSearchResults(Array.from(merged.values()).slice(0, SEARCH_LIMIT));
      } catch (err) {
        if (!cancelled) {
          console.error('Error searching exercises:', err);
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [dialogOpen, searchTerm]);

  const bookmarkedIds = useMemo(() => new Set(bookmarks.map((b) => b.exercise_id)), [bookmarks]);
  const visibleResults = useMemo(
    () => searchResults.filter((ex) => !bookmarkedIds.has(ex.id)),
    [searchResults, bookmarkedIds]
  );

  const handleAdd = async (exercise) => {
    setAddingId(exercise.id);
    try {
      const { error } = await supabase
        .from('user_exercise_bookmarks')
        .insert({ user_id: userId, exercise_id: exercise.id, sort_order: 0 });
      if (error) {
        if (error.code === '23505') {
          toast.info(`"${exercise.name}" is already bookmarked`);
          await fetchBookmarks();
          return;
        }
        throw error;
      }
      toast.success(`Bookmarked "${exercise.name}" for the athlete`);
      await fetchBookmarks();
    } catch (err) {
      console.error('Error adding exercise bookmark:', err);
      toast.error('Could not bookmark exercise', { description: err.message });
    } finally {
      setAddingId(null);
    }
  };

  const handleRemove = async (bookmark) => {
    const name = bookmark.exercises?.name || 'this exercise';
    if (!window.confirm(`Remove "${name}" from this athlete's bookmarks?`)) return;
    try {
      const { error } = await supabase
        .from('user_exercise_bookmarks')
        .delete()
        .eq('user_id', userId)
        .eq('exercise_id', bookmark.exercise_id);
      if (error) throw error;
      toast.success(`Removed "${name}"`);
      await fetchBookmarks();
    } catch (err) {
      console.error('Error removing exercise bookmark:', err);
      toast.error('Could not remove bookmark', { description: err.message });
    }
  };

  const closeDialog = (open) => {
    setDialogOpen(open);
    if (!open) {
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Bookmark className="h-4 w-4" />
              Bookmarked Exercises
              <Badge variant="secondary" className="text-xs ml-1">{bookmarks.length}</Badge>
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="h-3 w-3" />
              Add Bookmark
            </Button>
          </CardTitle>
          <CardDescription>
            Exercises saved to the athlete's exercise library bookmarks. The athlete can also add and remove their own.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
            </div>
          ) : bookmarks.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {bookmarks.map((bookmark) => (
                <div key={bookmark.id} className="rounded-md border bg-muted/30 flex items-center justify-between p-2.5">
                  <div className="min-w-0 flex-1 flex items-center gap-3">
                    <ExerciseThumb exercise={bookmark.exercises} />
                    <div className="min-w-0">
                      <span className="text-sm font-medium truncate block">{bookmark.exercises?.name || 'Unknown Exercise'}</span>
                      <ExerciseMeta exercise={bookmark.exercises}>
                        <span className="normal-case">Bookmarked {new Date(bookmark.created_at).toLocaleDateString()}</span>
                      </ExerciseMeta>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0 ml-3"
                    aria-label={`Remove bookmark ${bookmark.exercises?.name || 'exercise'}`}
                    onClick={() => handleRemove(bookmark)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No bookmarked exercises. Add as many as you like to the athlete's library.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Bookmark Exercises</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search exercises by name or alias…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>

            <div className="max-h-80 overflow-y-auto space-y-1">
              {searching ? (
                <div className="space-y-2 p-1">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
                </div>
              ) : visibleResults.length > 0 ? (
                visibleResults.map((exercise) => (
                  <button
                    key={exercise.id}
                    type="button"
                    className="w-full flex items-center justify-between gap-3 rounded-md p-2 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
                    disabled={addingId === exercise.id}
                    onClick={() => handleAdd(exercise)}
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-3">
                      <ExerciseThumb exercise={exercise} />
                      <div className="min-w-0">
                        <span className="text-sm font-medium truncate block">{exercise.name}</span>
                        <ExerciseMeta exercise={exercise} />
                      </div>
                    </div>
                    {addingId === exercise.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
                    ) : (
                      <BookmarkPlus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                ))
              ) : searchTerm.trim().length >= 2 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {searchResults.length > 0 ? 'All matching exercises are already bookmarked.' : 'No exercises found.'}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">Type at least 2 characters to search the library.</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Bookmarks are unlimited. The dialog stays open so you can add several in a row.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BookmarkedExercisesCard;
